import type { Creature, RewardOffer, RunState } from '~/types/game'
import { hasStarweave, createStarweaveRunState } from '~/utils/starweaveRun'
import { grantXp } from '~/utils/battle'
import { RELIC_POOL } from '~/data/relics'
import { KEEPSAKE_RELIC, KEEPSAKE_SKILL } from '~/data/keepsakes'
import { recordDeclinedOffers } from '~/utils/offerCycle'
import { generateOffers, rarityFloor } from '~/utils/rewards'
import { rollTrainingOptions } from '~/data/rewards'

/** Account purchases are frozen once, before the starting draft is rolled. */
export function initializeStarweaveRun(run: RunState, owned: readonly string[]): void {
  if (run.starweave || run.mode === 'daily' || typeof run.puzzleLevel === 'number') return
  run.starweave = createStarweaveRunState(owned)
  if (hasStarweave(run, 'nova-company')) run.maxRosterSize += 1
  if (hasStarweave(run, 'resolve-spark')) {
    run.maxHp += 5
    run.hp += 5
  }
}

export function starweaveDraftExtra(run: RunState): number {
  return hasStarweave(run, 'nova-company') ? 1 : 0
}

export function starweaveRecruitCatchUp(run: RunState, recruit: Creature): number {
  if (!hasStarweave(run, 'growth-signature') || run.starweave!.recruitBonusAct === run.act) return 0
  // The first recruit consumes this act even when already caught up.
  run.starweave!.recruitBonusAct = run.act
  const highest = Math.max(recruit.level, ...run.roster.map(c => c.level))
  const xp = Math.min(6, Math.max(0, highest - recruit.level) * 2)
  if (xp) grantXp(recruit, xp, run)
  return xp
}

export function starweaveRations(run: RunState, source: 'rest' | 'act'): number {
  if (!hasStarweave(run, 'resolve-right')) return 0
  if (source === 'rest') {
    if (run.starweave!.restBonusAct === run.act) return 0
    run.starweave!.restBonusAct = run.act
  }
  const amount = Math.max(0, ...run.roster.map(c => c.level))
  const healed = Math.min(Math.max(0, run.maxHp - run.hp), amount)
  run.hp += healed
  return healed
}

export function starweaveShopCashback(run: RunState, paid: number): number {
  if (!hasStarweave(run, 'fortune-left') || run.starweave!.cashbackAct === run.act) return 0
  run.starweave!.cashbackAct = run.act
  const refund = Math.min(10, Math.max(0, paid))
  run.gold += refund
  return refund
}

export function canStarweaveRelicReroll(run: RunState | null | undefined): boolean {
  if (!run || !hasStarweave(run, 'fortune-signature') || run.starweave!.relicRerollAct === run.act
    || run.starweave!.collectorPending) return false
  return run.phase === 'bonus' ? !!run.draft?.bonusOptions.some(id => id !== KEEPSAKE_RELIC)
    : !!run.pendingRewards?.some(o => o.pool === 'relic' && o.defId !== KEEPSAKE_RELIC) && !run.rewardResolution
}

export function rerollStarweaveRelics(run: RunState): boolean {
  if (!canStarweaveRelicReroll(run)) return false
  const starting = run.phase === 'bonus'
  const old: RewardOffer[] = starting
    ? run.draft!.bonusOptions.map(defId => ({ id: defId, defId, pool: 'relic' }))
    : run.pendingRewards!
  const ordinary = old.filter(o => o.pool === 'relic' && o.defId !== KEEPSAKE_RELIC)
  if (!ordinary.length) return false
  run.starweave!.relicRerollAct = run.act
  recordDeclinedOffers(run, 'relic', ordinary.map(o => o.defId))
  const node = run.map?.nodes.find(n => n.id === run.activeNodeId)
  const rolled = generateOffers('relic', run, starting ? null : rarityFloor(node?.type, 'relic'), node?.type,
    { count: ordinary.length, exclude: ordinary.map(o => o.defId) })
  const offers = [...rolled, ...old.filter(o => o.defId === KEEPSAKE_RELIC)]
  if (starting) run.draft!.bonusOptions = offers.filter(o => o.pool === 'relic').map(o => o.defId)
  else run.pendingRewards = offers
  return true
}

/**
 * 🎓 v6.85 Broader Curriculum (per Javi): once per run, one Training Mission
 * teaches TWO of its offered skills. Live while the nova is owned, the run's
 * double lesson is unspent, and the class is still waiting for a student.
 */
export function canStarweaveDoubleTraining(run: RunState | null | undefined, missionId: string): boolean {
  const mission = run?.encounter?.missions.find(m => m.id === missionId)
  return !!run && hasStarweave(run, 'nova-guiding') && run.starweave!.curriculumAct !== run.act
    && !!mission?.training && mission.status === 'waiting' && (mission.trainingOptions?.length ?? 0) >= 2
}

export function canStarweaveSkillReroll(run: RunState | null | undefined, missionId: string): boolean {
  const mission = run?.encounter?.missions.find(m => m.id === missionId)
  return !!run && hasStarweave(run, 'attunement-signature') && !run.starweave!.skillRerollUsed
    && !!mission?.training && mission.status === 'waiting' && !!mission.trainingOptions?.some(id => id !== KEEPSAKE_SKILL)
}

export function rerollStarweaveTraining(run: RunState, missionId: string): boolean {
  if (!canStarweaveSkillReroll(run, missionId)) return false
  const mission = run.encounter!.missions.find(m => m.id === missionId)!
  const old = mission.trainingOptions!
  const ordinary = old.filter(id => id !== KEEPSAKE_SKILL)
  if (!ordinary.length) return false
  run.starweave!.skillRerollUsed = true
  recordDeclinedOffers(run, 'skill', ordinary)
  mission.trainingOptions = [
    ...rollTrainingOptions(run, { mission: true, exclude: ordinary, preservePinned: true }),
    ...old.filter(id => id === KEEPSAKE_SKILL),
  ]
  mission.trainingChoice = undefined
  return true
}

/** Expose the same unchosen offer; deciding to skip preserves the act use. */
export function offerStarweaveCollector(run: RunState, offers: readonly RewardOffer[], source: 'starting' | 'reward'): boolean {
  if (!hasStarweave(run, 'nova-discovery') || run.starweave!.collectorAct === run.act
    || run.starweave!.collectorPending) return false
  const remaining = offers.filter(o => o.pool === 'relic' && !run.relics.includes(o.defId)
    && RELIC_POOL.some(r => r.id === o.defId))
  if (!remaining.length) return false
  run.starweave!.collectorPending = { source, offers: remaining.map(o => ({ ...o })) }
  return true
}

/** Debit before pickup effects, then remove the continuation to prevent recursion. */
export function consumeStarweaveCollector(run: RunState, relicId: string): boolean {
  if (!hasStarweave(run, 'nova-discovery') || run.starweave!.collectorAct === run.act
    || !run.starweave!.collectorPending?.offers.some(o => o.defId === relicId)
    || run.relics.includes(relicId)) return false
  run.starweave!.collectorAct = run.act
  delete run.starweave!.collectorPending
  return true
}
